import React, { useState } from 'react';
import { Scores } from '../../types';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  direction?: 'up' | 'down';
}

export function Tooltip({ content, children, direction = 'down' }: TooltipProps) {
  const [visible, setVisible] = useState(false);

  return (
    <button
      type="button"
      className="score-tooltip-wrapper"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={() => setVisible((v) => !v)}
    >
      {children}
      {visible && (
        <div className={`score-tooltip-box score-tooltip-box--${direction}`}>{content}</div>
      )}
    </button>
  );
}

interface BreakdownRowProps {
  label: string;
  value: number;
  hide?: boolean;
}

function BreakdownRow({ label, value, hide }: BreakdownRowProps) {
  if (hide && value === 0) return null;
  return (
    <div className="score-tooltip-row">
      <span className="score-tooltip-label">{label}</span>
      <span className={`score-tooltip-value ${value > 0 ? 'pos' : value < 0 ? 'neg' : ''}`}>
        {value > 0 ? `+${value}` : value}
      </span>
    </div>
  );
}

interface ScoreTooltipsProps {
  score: Scores;
  direction?: 'up' | 'down';
  label?: string;
}

export function ScoreTooltip({ score, direction }: ScoreTooltipsProps) {
  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Match Results</div>
          <BreakdownRow label="Wins" value={score.winsScore} hide />
          <BreakdownRow label="Draws" value={score.drawsScore} hide />
          <BreakdownRow label="Clean Sheets" value={score.cleanSheetsScore} hide />
          <BreakdownRow label="Red Cards" value={score.redCardsScore} hide />
          <BreakdownRow label="Own Goals" value={score.ownGoalsScore} hide />
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{score.score}</span>
    </Tooltip>
  );
}

export function GWTooltip({ score, direction }: ScoreTooltipsProps) {
  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Group Winners</div>
          <BreakdownRow label="Group Winner Pts" value={score.groupWinner} />
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{score.groupWinner}</span>
    </Tooltip>
  );
}

export function TotalTooltip({ score, direction, label }: ScoreTooltipsProps) {
  const grandTotal = score.score + score.groupWinner + score.goldenBoot
    + score.earliestGoal + score.latestGoal + score.hattrick
    + score.directFk + score.olimpico + score.keeperGoal
    + score.mostReds + score.teamGoals3plus;

  const displayLabel = label ?? (grandTotal >= 0 ? `+${grandTotal}` : `${grandTotal}`);

  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Total Breakdown</div>
          <BreakdownRow label="Match Results" value={score.score} hide />
          <BreakdownRow label="Group Winners" value={score.groupWinner} hide />
          <BreakdownRow label="Golden Boot" value={score.goldenBoot} hide />
          <BreakdownRow label="Fastest Goal" value={score.earliestGoal} hide />
          <BreakdownRow label="Latest Goal" value={score.latestGoal} hide />
          <BreakdownRow label="Hattrick" value={score.hattrick} hide />
          <BreakdownRow label="Direct FK" value={score.directFk} hide />
          <BreakdownRow label="Olimpico" value={score.olimpico} hide />
          <BreakdownRow label="Keeper Goal" value={score.keeperGoal} hide />
          <BreakdownRow label="Most Reds" value={score.mostReds} hide />
          <BreakdownRow label="Team 3+ Goals" value={score.teamGoals3plus} hide />
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{displayLabel}</span>
    </Tooltip>
  );
}

export function MatchTooltip({ score, direction, label }: ScoreTooltipsProps) {
  const total = score.winsScore + score.drawsScore + score.cleanSheetsScore
    + score.redCardsScore + score.ownGoalsScore + score.hattrick
    + score.directFk + score.olimpico + score.keeperGoal
    + score.mostReds + score.teamGoals3plus;

  const displayLabel = label ?? (total >= 0 ? `+${total}` : `${total}`);

  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Score Breakdown</div>
          <BreakdownRow label="Wins" value={score.winsScore} hide />
          <BreakdownRow label="Draws" value={score.drawsScore} hide />
          <BreakdownRow label="Clean Sheets" value={score.cleanSheetsScore} hide />
          <BreakdownRow label="Red Cards" value={score.redCardsScore} hide />
          <BreakdownRow label="Own Goals" value={score.ownGoalsScore} hide />
          <BreakdownRow label="Hattrick" value={score.hattrick} hide />
          <BreakdownRow label="Direct FK" value={score.directFk} hide />
          <BreakdownRow label="Olimpico" value={score.olimpico} hide />
          <BreakdownRow label="Keeper Goal" value={score.keeperGoal} hide />
          <BreakdownRow label="Most Reds" value={score.mostReds} hide />
          <BreakdownRow label="Team 3+ Goals" value={score.teamGoals3plus} hide />
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{displayLabel}</span>
    </Tooltip>
  );
}

export function GTooltip({ score, direction }: ScoreTooltipsProps) {
  const total = score.goldenBoot + score.earliestGoal + score.latestGoal
    + score.hattrick + score.directFk + score.olimpico
    + score.keeperGoal + score.mostReds + score.teamGoals3plus;

  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Bonuses</div>
          <BreakdownRow label="Golden Boot" value={score.goldenBoot} hide />
          <BreakdownRow label="Fastest Goal" value={score.earliestGoal} hide />
          <BreakdownRow label="Latest Goal" value={score.latestGoal} hide />
          <BreakdownRow label="Hattrick" value={score.hattrick} hide />
          <BreakdownRow label="Direct FK" value={score.directFk} hide />
          <BreakdownRow label="Olimpico" value={score.olimpico} hide />
          <BreakdownRow label="Keeper Goal" value={score.keeperGoal} hide />
          <BreakdownRow label="Most Reds" value={score.mostReds} hide />
          <BreakdownRow label="Team 3+ Goals" value={score.teamGoals3plus} hide />
          {total === 0 && <div className="score-tooltip-note">None yet</div>}
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{total}</span>
    </Tooltip>
  );
}

export function BonusesTooltip({ score, direction }: ScoreTooltipsProps) {
  const total = score.groupWinner + score.goldenBoot + score.earliestGoal + score.latestGoal
    + score.hattrick + score.directFk + score.olimpico
    + score.keeperGoal + score.mostReds + score.teamGoals3plus;

  return (
    <Tooltip
      direction={direction}
      content={(
        <div>
          <div className="score-tooltip-title">Bonuses</div>
          <BreakdownRow label="Group Winner" value={score.groupWinner} hide />
          <BreakdownRow label="Golden Boot" value={score.goldenBoot} hide />
          <BreakdownRow label="Fastest Goal" value={score.earliestGoal} hide />
          <BreakdownRow label="Latest Goal" value={score.latestGoal} hide />
          <BreakdownRow label="Hattrick" value={score.hattrick} hide />
          <BreakdownRow label="Direct FK" value={score.directFk} hide />
          <BreakdownRow label="Olimpico" value={score.olimpico} hide />
          <BreakdownRow label="Keeper Goal" value={score.keeperGoal} hide />
          <BreakdownRow label="Most Reds" value={score.mostReds} hide />
          <BreakdownRow label="Team 3+ Goals" value={score.teamGoals3plus} hide />
          {total === 0 && <div className="score-tooltip-note">None yet</div>}
        </div>
      )}
    >
      <span className="score-tooltip-trigger">{total}</span>
    </Tooltip>
  );
}
